import { BN, web3 } from "@project-serum/anchor";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  Token,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import {
  AccountMeta,
  PublicKey,
  SYSVAR_RENT_PUBKEY,
  Transaction,
} from "@solana/web3.js";
import dayjs from "dayjs";
import { divide } from "mathjs";

import {
  MILLISECONDS_PER_SECOND,
  UNQ_PER_AMOUNT,
} from "../../common/constants/common.constants";
import { STAKING_CAP_AMOUNT } from "../../common/constants/staking.constants";
import {
  Rarity,
  StakePeriodType,
  StakePeriodTypeSolana,
  StakingItemType,
  StakingStatusSolana,
} from "../../common/enums/staking.enum";
import {
  IMemberStakingInfo,
  IStakingAccountInfo,
} from "../../common/interfaces/staking.interface";
import { getRemainingAccountForMemberOfNFTClub } from "../common/helpers/nftClubs.helpers";
import {
  addCreateAtaIxAndGetAtaIfNeededForStakedNft,
  getAdditionalReward,
  getFixedRewardPercentage,
  getRemainingAccountsForFinishStaking,
  getStakingPeriodType,
  mapStakingStatusFromSolana,
} from "../common/helpers/staking.helpers";
import {
  governanceSeed,
  mint,
  programFactory,
  RPC_CONNECTION,
  splGovernanceProgram,
  stakeConfigSeed,
  stakedNftSeed,
  stakedTokensSeed,
  stakeRecordSeed,
  unqClubMemberSeed,
  unqClubSeed,
  unqTokensSeed,
} from "../common/utilities";
import { sendTransaction } from "../transactions/sendTransaction";
import { getMintMetadata } from "./general";

/**
 * Create staking account
 * @param realmName {string} Club name
 * @param wallet {AnchorWallet} Founder's wallet
 * @param stakeCountNumber {number} number of staking accounts in club data
 * @param stakeName {string} Name of staking account
 * @param stakePeriodType {StakePeriodType} Stake period type
 * @returns {Promise<PublicKey>} Public key of created staking config account
 */
export async function initializeStaking(
  realmName: string,
  wallet: AnchorWallet,
  stakeCountNumber: number,
  stakeName: string,
  stakePeriodType: StakePeriodType
): Promise<PublicKey> {
  try {
    const clubProgram = programFactory();
    const stakeCount = Buffer.alloc(4);
    stakeCount.writeUInt32LE(stakeCountNumber + 1, 0);
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );
    const [memberDataAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );
    const [stakeConfigAddress] = await PublicKey.findProgramAddress(
      [unqClubSeed, clubData.toBuffer(), stakeConfigSeed, stakeCount],
      clubProgram.programId
    );
    const [stakedTokens] = await PublicKey.findProgramAddress(
      [unqClubSeed, stakeConfigAddress.toBuffer(), stakedTokensSeed],
      clubProgram.programId
    );
    const recentBlockhash = (await RPC_CONNECTION.getLatestBlockhash())
      .blockhash;
    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash,
    });

    const stakingAccounts = await getAllStakingAccountsForClub(
      realmName,
      stakeCountNumber
    );
    if (stakingAccounts && stakingAccounts.length > 0) {
      for (const stakingAccount of stakingAccounts) {
        const currentUnixTimestapm = divide(
          dayjs().valueOf(),
          MILLISECONDS_PER_SECOND
        );
        if (
          stakingAccount.status === 1 &&
          stakingAccount.stakePeriodEnd &&
          currentUnixTimestapm > stakingAccount.stakePeriodEnd
        ) {
          let nftTokenAccount;
          if (stakingAccount.nftStakeRecord) {
            const { tokenAccount, transactionIx } =
              await addCreateAtaIxAndGetAtaIfNeededForStakedNft(
                stakingAccount,
                stakingAccount.nftStakeRecord?.nftOwner,
                wallet
              );
            nftTokenAccount = tokenAccount;
            transactionIx.forEach((item) => tx.add(item));
          }
          const stakeCount = Buffer.alloc(4);
          stakeCount.writeUInt32LE(stakingAccount.stakingIndex, 0);
          const [stakeConfigAddress] = await PublicKey.findProgramAddress(
            [unqClubSeed, clubData.toBuffer(), stakeConfigSeed, stakeCount],
            clubProgram.programId
          );
          const [stakedTokens] = await PublicKey.findProgramAddress(
            [unqClubSeed, stakeConfigAddress.toBuffer(), stakedTokensSeed],
            clubProgram.programId
          );
          const [clubUnqTokensAddress] = await PublicKey.findProgramAddress(
            [unqClubSeed, mint.toBuffer(), unqTokensSeed],
            clubProgram.programId
          );
          const finishStakingIx = clubProgram.instruction.finishStaking(
            stakingAccount.stakingIndex,
            {
              accounts: {
                clubData,
                memberData: memberDataAddress,
                payer: wallet.publicKey,
                stakedTokens,
                stakeConfig: stakingAccount.accountAddress,
                clubUnqTokens: clubUnqTokensAddress,
                tokenProgram: TOKEN_PROGRAM_ID,
                systemProgram: web3.SystemProgram.programId,
              },
              remainingAccounts: getRemainingAccountsForFinishStaking(
                stakingAccount,
                nftTokenAccount
              ),
            }
          );
          tx.add(finishStakingIx);
        }
      }
    }

    const initializeStakingIx = clubProgram.instruction.initializeStaking(
      stakeName,
      new BN(STAKING_CAP_AMOUNT * UNQ_PER_AMOUNT),
      stakePeriodType, // TODO@milica: this is only for testing, should be changed to stakePeriodType
      {
        accounts: {
          clubData,
          memberData: memberDataAddress,
          payer: wallet.publicKey,
          stakedTokens,
          stakeConfig: stakeConfigAddress,
          unqMint: mint,
          rent: web3.SYSVAR_RENT_PUBKEY,
          tokenProgram: TOKEN_PROGRAM_ID,
          systemProgram: web3.SystemProgram.programId,
        },
      }
    );

    tx.add(initializeStakingIx);
    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet,
    });
    return stakeConfigAddress;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Start staking period for selected staking account
 * @param realmName {string} Club name
 * @param wallet {AnchorWallet} Founder's wallet
 * @param stakeIndex {number} Index of selected staking
 */
export async function startStaking(
  realmName: string,
  wallet: AnchorWallet,
  stakeIndex: number
) {
  try {
    const clubProgram = programFactory();
    const stakeCount = Buffer.alloc(4);
    stakeCount.writeUInt32LE(stakeIndex, 0);
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );
    const [memberDataAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );
    const [stakeConfigAddress] = await PublicKey.findProgramAddress(
      [unqClubSeed, clubData.toBuffer(), stakeConfigSeed, stakeCount],
      clubProgram.programId
    );
    const [stakedTokens] = await PublicKey.findProgramAddress(
      [unqClubSeed, stakeConfigAddress.toBuffer(), stakedTokensSeed],
      clubProgram.programId
    );
    const startStakingIx = clubProgram.instruction.startStaking(stakeIndex, {
      accounts: {
        clubData,
        memberData: memberDataAddress,
        payer: wallet.publicKey,
        stakedTokens,
        stakeConfig: stakeConfigAddress,
        unqMint: mint,
        tokenProgram: TOKEN_PROGRAM_ID,
        systemProgram: web3.SystemProgram.programId,
      },
    });
    const recentBlockhash = (await RPC_CONNECTION.getLatestBlockhash())
      .blockhash;
    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash,
    });
    tx.add(startStakingIx);
    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Cancel selected staking account
 * @param realmName {string} Club name
 * @param stakingAccount {IStakingAccountInfo} Selected staking account
 * @param wallet {AnchorWallet} Founder's wallet
 */
export async function cancelStaking(
  realmName: string,
  oldStakingAccount: IStakingAccountInfo,
  wallet: AnchorWallet
) {
  try {
    const clubProgram = programFactory();
    const stakeCount = Buffer.alloc(4);

    const stakingAccount = await getInfoForStakingAccount(
      oldStakingAccount.accountAddress
    );

    stakeCount.writeUInt32LE(stakingAccount.stakingIndex, 0);
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );
    const [memberDataAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );

    const [stakedTokens] = await PublicKey.findProgramAddress(
      [unqClubSeed, stakingAccount.accountAddress.toBuffer(), stakedTokensSeed],
      clubProgram.programId
    );
    const [clubUnqTokensAddress] = await PublicKey.findProgramAddress(
      [unqClubSeed, mint.toBuffer(), unqTokensSeed],
      clubProgram.programId
    );

    const recentBlockhash = (await RPC_CONNECTION.getLatestBlockhash())
      .blockhash;
    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash,
    });

    let nftTokenAccount;
    if (stakingAccount.nftStakeRecord) {
      const { tokenAccount, transactionIx } =
        await addCreateAtaIxAndGetAtaIfNeededForStakedNft(
          stakingAccount,
          stakingAccount.nftStakeRecord?.nftOwner,
          wallet
        );
      nftTokenAccount = tokenAccount;
      transactionIx.forEach((item) => tx.add(item));
    }

    const cancelStakingIx = clubProgram.instruction.finishStaking(
      stakingAccount.stakingIndex,
      {
        accounts: {
          clubData,
          memberData: memberDataAddress,
          payer: wallet.publicKey,
          stakedTokens,
          stakeConfig: stakingAccount.accountAddress,
          clubUnqTokens: clubUnqTokensAddress,
          tokenProgram: TOKEN_PROGRAM_ID,
          systemProgram: web3.SystemProgram.programId,
        },
        remainingAccounts: getRemainingAccountsForFinishStaking(
          stakingAccount,
          nftTokenAccount
        ),
      }
    );

    tx.add(cancelStakingIx);

    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Get initial list of staking accounts for the club
 * @param realmName {string} Club name
 * @param treasury {ClubTreasury} Club treasury
 * @returns {IStakingAccountInfo[]} Array of staking accounts
 */
export async function getAllStakingAccountsForClub(
  realmName: string,
  stakeCount: number
): Promise<IStakingAccountInfo[]> {
  try {
    const stakeConfigs: PublicKey[] = [];
    const clubProgram = programFactory();
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    for (let i = 0; i < stakeCount; i++) {
      const stakeCount = Buffer.alloc(4);
      stakeCount.writeUInt32LE(i + 1, 0);
      const [stakeConfigAddress] = await PublicKey.findProgramAddress(
        [unqClubSeed, clubData.toBuffer(), stakeConfigSeed, stakeCount],
        clubProgram.programId
      );
      stakeConfigs.push(stakeConfigAddress);
    }
    const stakeConfigAccounts: any[] =
      await clubProgram.account.stakeConfig.fetchMultiple(stakeConfigs);
    const stakeConfigsInfo: IStakingAccountInfo[] = [];
    for (const [index, stakeConfigInfo] of stakeConfigAccounts.entries()) {
      //Potential problem with null stakeConfigInfo when creating new staking
      if (!stakeConfigInfo) continue;

      const nftMetadata =
        stakeConfigInfo.nftStakeRecord &&
        (await getMintMetadata(stakeConfigInfo.nftStakeRecord.tokenMint));
      stakeConfigsInfo.push({
        stakingRewardPercentage: getFixedRewardPercentage(
          Object.keys(
            stakeConfigInfo.stakePeriodType
          )[0] as StakePeriodTypeSolana
        ),
        stakingPeriodType: getStakingPeriodType(
          Object.keys(
            stakeConfigInfo.stakePeriodType
          )[0] as StakePeriodTypeSolana
        ),
        accountAddress: stakeConfigs[index],
        capAmount: stakeConfigInfo.capAmount.toNumber() / UNQ_PER_AMOUNT,
        name: stakeConfigInfo.name,
        raisedAmount: stakeConfigInfo.raisedAmount.toNumber() / UNQ_PER_AMOUNT,
        status: mapStakingStatusFromSolana(
          Object.keys(stakeConfigInfo.status)[0] as StakingStatusSolana
        ),
        stakingIndex: stakeConfigInfo.stakeIndex,
        stakePeriodEnd: stakeConfigInfo.stakePeriodEnd
          ? (stakeConfigInfo.stakePeriodEnd as number)
          : undefined,
        nftStakeRecord: stakeConfigInfo.nftStakeRecord && {
          additionalReward: getAdditionalReward(
            Object.keys(stakeConfigInfo.nftStakeRecord.rarity)[0] as Rarity
          ),
          hasClaimed: stakeConfigInfo.nftStakeRecord.hasClaimed,
          membersTokenAccount: stakeConfigInfo.nftStakeRecord.tokenAccount,
          nftOwner: stakeConfigInfo.nftStakeRecord.nftOwner,
          stakedNftTokenAccount: stakeConfigInfo.nftStakeRecord.stakedNft,
          unstakePeriodEnd: stakeConfigInfo.nftStakeRecord.unstakePeriodEnd,
          mint: stakeConfigInfo.nftStakeRecord.tokenMint,
          rarity: Object.keys(
            stakeConfigInfo.nftStakeRecord.rarity
          )[0] as Rarity,
          nftImgUrl: nftMetadata.image,
          nftName: nftMetadata.name,
        },
      });
    }
    console.log(stakeConfigsInfo);
    return stakeConfigsInfo;
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Get info for selected staking account
 * @param stakingAccountPubKey {PublicKey} Public key of selected stakig account
 * @returns {IStakingAccountInfo} Staking account info
 */
export async function getInfoForStakingAccount(
  stakingAccountPubKey: PublicKey
): Promise<IStakingAccountInfo> {
  try {
    const clubProgram = programFactory();
    const stakeConfigAccount: any = await clubProgram.account.stakeConfig.fetch(
      stakingAccountPubKey
    );
    const nftMetadata =
      stakeConfigAccount.nftStakeRecord &&
      (await getMintMetadata(stakeConfigAccount.nftStakeRecord.tokenMint));

    return {
      accountAddress: stakingAccountPubKey,
      stakingRewardPercentage: getFixedRewardPercentage(
        Object.keys(
          stakeConfigAccount.stakePeriodType
        )[0] as StakePeriodTypeSolana
      ),
      stakingPeriodType: getStakingPeriodType(
        Object.keys(
          stakeConfigAccount.stakePeriodType
        )[0] as StakePeriodTypeSolana
      ),
      capAmount: stakeConfigAccount.capAmount.toNumber() / UNQ_PER_AMOUNT,
      name: stakeConfigAccount.name,
      raisedAmount: stakeConfigAccount.raisedAmount.toNumber() / UNQ_PER_AMOUNT,
      status: mapStakingStatusFromSolana(
        Object.keys(stakeConfigAccount.status)[0] as StakingStatusSolana
      ),
      stakingIndex: stakeConfigAccount.stakeIndex,
      stakePeriodEnd: stakeConfigAccount.stakePeriodEnd
        ? (stakeConfigAccount.stakePeriodEnd as number)
        : undefined,
      nftStakeRecord: stakeConfigAccount.nftStakeRecord && {
        additionalReward: getAdditionalReward(
          Object.keys(stakeConfigAccount.nftStakeRecord.rarity)[0] as Rarity
        ),
        hasClaimed: stakeConfigAccount.nftStakeRecord.hasClaimed,
        membersTokenAccount: stakeConfigAccount.nftStakeRecord.tokenAccount,
        nftOwner: stakeConfigAccount.nftStakeRecord.nftOwner,
        stakedNftTokenAccount: stakeConfigAccount.nftStakeRecord.stakedNft,
        unstakePeriodEnd: stakeConfigAccount.nftStakeRecord.unstakePeriodEnd,
        mint: stakeConfigAccount.nftStakeRecord.tokenMint,
        rarity: Object.keys(
          stakeConfigAccount.nftStakeRecord.rarity
        )[0] as Rarity,
        nftImgUrl: nftMetadata.image,
        nftName: nftMetadata.name,
      },
    };
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Stake tokens
 * @param realmName {string} Club name
 * @param stakingAccount {IStakingAccountInfo} Selected staking account
 * @param wallet {AnchorWallet} User's wallet
 * @param memberTokenAccount {PublicKey} User's UNQ token account
 * @param amount {number} Amount of tokens for staking
 */
export async function stakeTokens(
  realmName: string,
  oldStakingAccount: IStakingAccountInfo,
  wallet: AnchorWallet,
  amount: number,
  memberTokenAccount?: PublicKey,
  nftStakeRemainingAccounts?: AccountMeta[]
  //TODO@milica-monke-merge: only for monke
) {
  try {
    const clubProgram = programFactory();
    const remainingAccounts: AccountMeta[] = [];

    //TODO@milica-monke-merge: this check is only for monke

    const stakingAccount = await getInfoForStakingAccount(
      oldStakingAccount.accountAddress
    );

    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );
    const [memberDataAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );
    const stakeCount = Buffer.alloc(4);
    stakeCount.writeInt32LE(stakingAccount.stakingIndex, 0);
    const [stakeConfigAddress] = await PublicKey.findProgramAddress(
      [unqClubSeed, clubData.toBuffer(), stakeConfigSeed, stakeCount],
      clubProgram.programId
    );

    const [stakedTokens] = await PublicKey.findProgramAddress(
      [unqClubSeed, stakeConfigAddress.toBuffer(), stakedTokensSeed],
      clubProgram.programId
    );
    const [stakeRecord] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        wallet.publicKey.toBuffer(),
        stakeRecordSeed,
        stakeConfigAddress.toBuffer(),
      ],
      clubProgram.programId
    );

    const recentBlockhash = (await RPC_CONNECTION.getLatestBlockhash())
      .blockhash;
    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash,
    });

    //PREPARE REMAINING ACCOUNTS
    if (nftStakeRemainingAccounts) {
      nftStakeRemainingAccounts.forEach((item) => {
        remainingAccounts.push(item);
      });
      const [stakedNft] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          nftStakeRemainingAccounts[1].pubkey.toBuffer(), //MINT
          stakedNftSeed,
        ],
        clubProgram.programId
      );

      remainingAccounts.push(
        { pubkey: stakedNft, isWritable: true, isSigner: false },
        { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false }
      );
      if (stakingAccount.nftStakeRecord) {
        let nftTokenAccount;
        if (stakingAccount.nftStakeRecord) {
          const { tokenAccount, transactionIx } =
            await addCreateAtaIxAndGetAtaIfNeededForStakedNft(
              stakingAccount,
              stakingAccount.nftStakeRecord?.nftOwner,
              wallet
            );
          nftTokenAccount = tokenAccount;
          transactionIx.forEach((item) => tx.add(item));
        }

        remainingAccounts.push(
          {
            pubkey: stakingAccount.nftStakeRecord.stakedNftTokenAccount,
            isSigner: false,
            isWritable: true,
          },
          {
            pubkey:
              nftTokenAccount ??
              stakingAccount.nftStakeRecord.membersTokenAccount,
            isSigner: false,
            isWritable: true,
          },
          {
            pubkey: stakingAccount.nftStakeRecord.nftOwner,
            isSigner: false,
            isWritable: true,
          }
        );
      }
    } else {
      memberTokenAccount &&
        remainingAccounts.push(
          {
            pubkey: stakeRecord,
            isWritable: true,
            isSigner: false,
          },
          {
            pubkey: memberTokenAccount,
            isWritable: true,
            isSigner: false,
          },
          {
            pubkey: stakedTokens,
            isWritable: true,
            isSigner: false,
          }
        );
    }

    const stakeTokensIx = clubProgram.instruction.stakeTokens(
      stakingAccount.stakingIndex,
      new BN(amount * UNQ_PER_AMOUNT),
      nftStakeRemainingAccounts ? StakingItemType.NFT : StakingItemType.FT,
      {
        accounts: {
          clubData,
          memberData: memberDataAddress,
          payer: wallet.publicKey,
          stakeConfig: stakeConfigAddress,
          systemProgram: web3.SystemProgram.programId,
          tokenProgram: TOKEN_PROGRAM_ID,
          realm: realmAddress,
        },
        remainingAccounts,
      }
    );
    tx.add(stakeTokensIx);

    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Unstake tokens
 * @param realmName {string} Club name
 * @param stakingAccount {IStakingAccountInfo} Selected staking account
 * @param memberTokenAccount {PublicKey} User's UNQ token account
 * @param wallet {AnchorWallet} User's wallet
 */
export async function unstakeTokens(
  realmName: string,
  stakingAccount: IStakingAccountInfo,
  memberTokenAccount: PublicKey | undefined,
  wallet: AnchorWallet,
  nftUnstaking?: boolean
) {
  try {
    const clubProgram = programFactory();
    const remainingAccounts: AccountMeta[] = [];
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );
    const [memberDataAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );
    const [stakeRecord] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        wallet.publicKey.toBuffer(),
        stakeRecordSeed,
        stakingAccount.accountAddress.toBuffer(),
      ],
      clubProgram.programId
    );
    if (!nftUnstaking) {
      remainingAccounts.push({
        pubkey: stakeRecord,
        isSigner: false,
        isWritable: true,
      });
    }
    const recentBlockhash = (await RPC_CONNECTION.getLatestBlockhash())
      .blockhash;
    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash,
    });
    const unstakeTokensIx = clubProgram.instruction.unstakeTokens(
      stakingAccount.stakingIndex,
      nftUnstaking ? StakingItemType.NFT : StakingItemType.FT,
      {
        accounts: {
          clubData,
          memberData: memberDataAddress,
          payer: wallet.publicKey,
          stakeConfig: stakingAccount.accountAddress,
          systemProgram: web3.SystemProgram.programId,
        },
        remainingAccounts,
      }
    );

    tx.add(unstakeTokensIx);
    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}

/**
 * Claim tokens
 * @param realmName {string} Club name
 * @param stakingAccount {IStakingAccountInfo} Selected staking account
 * @param memberTokenAccount {PublicKey} User's UNQ token account
 * @param wallet {AnchorWallet} User's wallet
 */
export async function claimTokens(
  realmName: string,
  oldStakingAccount: IStakingAccountInfo,
  memberTokenAccount: PublicKey | undefined,
  wallet: AnchorWallet,
  claimNft: boolean,
  memberStakingInfo: IMemberStakingInfo | undefined
) {
  try {
    const clubProgram = programFactory();
    const remainingAccounts: AccountMeta[] = [];

    const stakingAccount = await getInfoForStakingAccount(
      oldStakingAccount.accountAddress
    );

    let unqTokenAccount = memberTokenAccount;
    let nftMembersTokenAccount =
      stakingAccount.nftStakeRecord?.membersTokenAccount;
    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [memberDataAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );
    const [stakedTokens] = await PublicKey.findProgramAddress(
      [unqClubSeed, stakingAccount.accountAddress.toBuffer(), stakedTokensSeed],
      clubProgram.programId
    );
    const [stakeRecord] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        wallet.publicKey.toBuffer(),
        stakeRecordSeed,
        stakingAccount.accountAddress.toBuffer(),
      ],
      clubProgram.programId
    );
    const recentBlockhash = (await RPC_CONNECTION.getLatestBlockhash())
      .blockhash;
    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash,
    });
    const currentUnixTimestapm = divide(
      dayjs().valueOf(),
      MILLISECONDS_PER_SECOND
    );

    let shouldFinish = true;
    if (
      stakingAccount.nftStakeRecord &&
      stakingAccount.nftStakeRecord.unstakePeriodEnd &&
      !stakingAccount.nftStakeRecord.hasClaimed &&
      stakingAccount.nftStakeRecord.nftOwner.toString() ===
        wallet.publicKey.toString() &&
      claimNft
    ) {
      shouldFinish = false;
    }

    if (
      stakingAccount.status === 1 &&
      stakingAccount.stakePeriodEnd &&
      currentUnixTimestapm > stakingAccount.stakePeriodEnd &&
      shouldFinish
    ) {
      let nftTokenAccount;
      if (stakingAccount.nftStakeRecord) {
        const { tokenAccount, transactionIx } =
          await addCreateAtaIxAndGetAtaIfNeededForStakedNft(
            stakingAccount,
            stakingAccount.nftStakeRecord?.nftOwner,
            wallet
          );
        nftTokenAccount = tokenAccount;
        transactionIx.forEach((item) => tx.add(item));
      }
      const [clubUnqTokensAddress] = await PublicKey.findProgramAddress(
        [unqClubSeed, mint.toBuffer(), unqTokensSeed],
        clubProgram.programId
      );
      const finishStakingIx = clubProgram.instruction.finishStaking(
        stakingAccount.stakingIndex,
        {
          accounts: {
            clubData,
            memberData: memberDataAddress,
            payer: wallet.publicKey,
            stakedTokens,
            stakeConfig: stakingAccount.accountAddress,
            clubUnqTokens: clubUnqTokensAddress,
            tokenProgram: TOKEN_PROGRAM_ID,
            systemProgram: web3.SystemProgram.programId,
          },
          remainingAccounts: getRemainingAccountsForFinishStaking(
            stakingAccount,
            nftTokenAccount
          ),
        }
      );
      tx.add(finishStakingIx);
    }

    if (unqTokenAccount === undefined && !claimNft) {
      unqTokenAccount = await Token.getAssociatedTokenAddress(
        ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
        TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
        mint, // mint
        wallet.publicKey, // owner
        false
      );
      tx.add(
        Token.createAssociatedTokenAccountInstruction(
          ASSOCIATED_TOKEN_PROGRAM_ID, // always ASSOCIATED_TOKEN_PROGRAM_ID
          TOKEN_PROGRAM_ID, // always TOKEN_PROGRAM_ID
          mint, // mint
          unqTokenAccount, // ata
          wallet.publicKey, // owner of token account
          wallet.publicKey // fee payer
        )
      );
    }

    if (!claimNft)
      remainingAccounts.push({
        pubkey: stakeRecord,
        isSigner: false,
        isWritable: true,
      });

    let stakedNft;
    if (claimNft && stakingAccount.nftStakeRecord) {
      const [stakedNftAddress] = await PublicKey.findProgramAddress(
        [
          unqClubSeed,
          stakingAccount.nftStakeRecord?.mint.toBuffer(),
          stakedNftSeed,
        ],
        clubProgram.programId
      );
      stakedNft = stakedNftAddress;
      const { tokenAccount, transactionIx } =
        await addCreateAtaIxAndGetAtaIfNeededForStakedNft(
          stakingAccount,
          wallet.publicKey,
          wallet
        );
      nftMembersTokenAccount = tokenAccount;
      transactionIx.forEach((item) => tx.add(item));
    }
    if (
      claimNft ||
      (!claimNft && memberStakingInfo && memberStakingInfo.depositAmount > 0)
    ) {
      const claimTokensIx = clubProgram.instruction.claimStakedTokens(
        stakingAccount.stakingIndex,
        claimNft ? StakingItemType.NFT : StakingItemType.FT,
        {
          accounts: {
            clubData,
            memberData: memberDataAddress,
            stakeConfig: stakingAccount.accountAddress,
            stakedTokens:
              claimNft && stakingAccount.nftStakeRecord && stakedNft
                ? stakedNft
                : stakedTokens,
            memberTokens:
              claimNft &&
              stakingAccount.nftStakeRecord &&
              nftMembersTokenAccount
                ? nftMembersTokenAccount
                : unqTokenAccount!,
            payer: wallet.publicKey,
            systemProgram: web3.SystemProgram.programId,
            tokenProgram: TOKEN_PROGRAM_ID,
          },
          remainingAccounts,
        }
      );
      tx.add(claimTokensIx);
    }

    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export async function finishStaking(
  realmName: string,
  oldStakingAccount: IStakingAccountInfo,
  wallet: AnchorWallet
) {
  try {
    const clubProgram = programFactory();

    const stakingAccount = await getInfoForStakingAccount(
      oldStakingAccount.accountAddress
    );

    const [realmAddress] = await PublicKey.findProgramAddress(
      [governanceSeed, Buffer.from(realmName)],
      splGovernanceProgram
    );
    const [clubData] = await PublicKey.findProgramAddress(
      [unqClubSeed, realmAddress.toBuffer()],
      clubProgram.programId
    );

    const [memberDataAddress] = await PublicKey.findProgramAddress(
      [
        unqClubSeed,
        clubData.toBuffer(),
        unqClubMemberSeed,
        wallet.publicKey.toBuffer(),
      ],
      clubProgram.programId
    );
    const [stakedTokens] = await PublicKey.findProgramAddress(
      [unqClubSeed, stakingAccount.accountAddress.toBuffer(), stakedTokensSeed],
      clubProgram.programId
    );
    const recentBlockhash = (await RPC_CONNECTION.getLatestBlockhash())
      .blockhash;
    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash,
    });
    const [clubUnqTokensAddress] = await PublicKey.findProgramAddress(
      [unqClubSeed, mint.toBuffer(), unqTokensSeed],
      clubProgram.programId
    );

    let nftTokenAccount;
    if (stakingAccount.nftStakeRecord) {
      const { tokenAccount, transactionIx } =
        await addCreateAtaIxAndGetAtaIfNeededForStakedNft(
          stakingAccount,
          stakingAccount.nftStakeRecord?.nftOwner,
          wallet
        );
      nftTokenAccount = tokenAccount;
      transactionIx.forEach((item) => tx.add(item));
    }
    const finishStakingIx = clubProgram.instruction.finishStaking(
      stakingAccount.stakingIndex,
      {
        accounts: {
          clubData,
          memberData: memberDataAddress,
          payer: wallet.publicKey,
          stakedTokens,
          stakeConfig: stakingAccount.accountAddress,
          clubUnqTokens: clubUnqTokensAddress,
          tokenProgram: TOKEN_PROGRAM_ID,
          systemProgram: web3.SystemProgram.programId,
        },
        remainingAccounts: getRemainingAccountsForFinishStaking(
          stakingAccount,
          nftTokenAccount
        ),
      }
    );
    tx.add(finishStakingIx);

    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      connection: RPC_CONNECTION,
      transaction: signedTx,
      wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export const getUpdatedStakeCountForClub = async (
  clubAddress: string
): Promise<number> => {
  try {
    const clubProgram = programFactory();
    const club = await clubProgram.account.clubData.fetch(
      new PublicKey(clubAddress)
    );

    return club.stakeCount;
  } catch (error) {
    console.log(error);
    throw error;
  }
};
